import React, { useState } from "react";

/**
 * Gallery / Before & After
 * Editorial masonry of real renovation projects.
 * Each tile has a hover-driven "BEFORE → AFTER" reveal slider (CSS-only).
 */

const PROJECTS = [
  {
    id: "tor-kitchen",
    title: "Mid-century kitchen revival",
    location: "Toronto, ON",
    span: "lg:col-span-5 lg:row-span-2",
    aspect: "aspect-[4/5]",
    before: "/projects/tor-kitchen-before.png",
    after: "/projects/tor-kitchen-after.png",
  },
  {
    id: "miss-bath",
    title: "Spa-grade ensuite",
    location: "Mississauga, ON",
    span: "lg:col-span-4",
    aspect: "aspect-[5/4]",
    before: "/projects/miss-bath-before.png",
    after: "/projects/miss-bath-after.png",
  },
  {
    id: "etob-base",
    title: "Walk-out basement suite",
    location: "Etobicoke, ON",
    span: "lg:col-span-3",
    aspect: "aspect-square",
    before: "/projects/etob-base-before.png",
    after: "/projects/etob-base-after.png",
  },
  {
    id: "osh-deck",
    title: "Cedar back deck + pergola",
    location: "Oshawa, ON",
    span: "lg:col-span-4",
    aspect: "aspect-[5/4]",
    before: "/projects/osh-deck-before.png",
    after: "/projects/osh-deck-after.png",
  },
  {
    id: "rh-add",
    title: "Second-storey addition",
    location: "Richmond Hill, ON",
    span: "lg:col-span-3",
    aspect: "aspect-square",
    before: "/projects/rh-add-before.png",
    after: "/projects/rh-add-after.png",
  },
];

const Tile = ({ project }) => {
  const [hover, setHover] = useState(false);
  const { title, location, span, aspect, before, after } = project;

  return (
    <article
      className={`group relative overflow-hidden ${span}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => setHover((v) => !v)}
    >
      {/* Frame */}
      <div className={`relative w-full ${aspect} overflow-hidden bg-slate-200`}>
        {/* AFTER (base layer — finished, full-color) */}
        <img
          src={after}
          alt={`${title} — after`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
        />

        {/* BEFORE (clipped reveal layer — real before photo with subtle tonal treatment) */}
        <div
          className="absolute inset-0 overflow-hidden transition-[width] duration-[900ms] ease-[cubic-bezier(.65,.05,.36,1)]"
          style={{ width: hover ? "0%" : "100%" }}
        >
          <img
            src={before}
            alt={`${title} — before`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ minWidth: "100%" }}
          />
          {/* very subtle warm overlay to keep visual continuity */}
          <div className="absolute inset-0 bg-black/5" />
        </div>

        {/* Vertical reveal divider line */}
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white/90 shadow-[0_0_10px_rgba(0,0,0,0.35)] transition-[left] duration-[900ms] ease-[cubic-bezier(.65,.05,.36,1)] pointer-events-none"
          style={{ left: hover ? "calc(0% - 1px)" : "calc(100% - 1px)" }}
        />

        {/* Drag-handle indicator */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center transition-[left] duration-[900ms] ease-[cubic-bezier(.65,.05,.36,1)] pointer-events-none"
          style={{ left: hover ? "calc(0% - 18px)" : "calc(100% - 18px)" }}
        >
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
            <path
              d="M4 1 L1 5 L4 9 M10 1 L13 5 L10 9"
              stroke="#0f172a"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* BEFORE / AFTER labels */}
        <span
          className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-[0.18em] uppercase bg-white/85 text-[#0f172a] backdrop-blur-sm transition-opacity duration-300"
          style={{ opacity: hover ? 0 : 1 }}
        >
          Before
        </span>
        <span
          className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-[0.18em] uppercase text-white transition-opacity duration-300"
          style={{
            background: "#F26B1F",
            opacity: hover ? 1 : 0,
            boxShadow: "0 6px 14px -6px rgba(242,107,31,0.55)",
          }}
        >
          After
        </span>

        {/* Project meta — bottom */}
        <div className="absolute inset-x-0 bottom-0 p-5 lg:p-6 bg-gradient-to-t from-black/65 via-black/25 to-transparent">
          <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/75">
            {location}
          </p>
          <h3 className="mt-1 text-white font-extrabold text-[17px] sm:text-[19px] leading-[1.2] tracking-tight">
            {title}
          </h3>
        </div>
      </div>
    </article>
  );
};

const GallerySection = () => {
  return (
    <section
      data-testid="gallery-section"
      className="relative w-full bg-white pt-16 sm:pt-24 lg:pt-32 pb-16 sm:pb-24 lg:pb-32 border-t border-slate-200"
    >
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-end mb-14 lg:mb-20">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="block h-px w-8 bg-[#1AA0DD]" />
              <span className="text-[11px] sm:text-[12px] font-bold tracking-[0.24em] uppercase text-[#1AA0DD]">
                Project gallery
              </span>
            </div>
            <h2
              className="font-extrabold text-[#0f172a] tracking-tight text-[28px] sm:text-[40px] lg:text-[48px] leading-[1.05]"
              style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
            >
              Before. After.{" "}
              <span style={{ color: "#F26B1F" }}>Receipts.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-2">
            <p className="text-[#475569] text-[15px] sm:text-[16px] leading-[1.7] max-w-[440px]">
              Hover any project to see the transformation. Every photo is a
              real MeinHaus build, signed off by a homeowner — never staged,
              never AI-generated.
            </p>
          </div>
        </div>

        {/* Masonry */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-5 lg:auto-rows-fr">
          {PROJECTS.map((p) => (
            <Tile key={p.id} project={p} />
          ))}
        </div>

        {/* Footer link */}
        <div className="mt-12 flex items-center justify-center">
          <a
            href="#all-projects"
            data-testid="gallery-cta-all"
            className="group inline-flex items-center gap-2 text-[14px] font-semibold text-[#0f172a] hover:text-[#1AA0DD] transition-colors"
          >
            See all 240+ completed projects
            <svg
              width="20"
              height="10"
              viewBox="0 0 20 10"
              fill="none"
              className="transition-transform duration-300 group-hover:translate-x-1"
              aria-hidden="true"
            >
              <path
                d="M1 5 H17 M13 1 L17 5 L13 9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
